import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ListChecks,
  Search,
  RefreshCw,
  Phone,
  Building2,
  User,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Package,
  Filter,
  Eye,
  ExternalLink,
  Check,
  X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const PickPool = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ['admin','super_admin'].includes(user?.role);
  const [pickPoolData, setPickPoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  // LFB Dialog state
  const [lfbDialogOpen, setLfbDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [lfbId, setLfbId] = useState("");
  const [submittingLfb, setSubmittingLfb] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchPickPool();
  }, [debouncedSearch]);

  const fetchPickPool = async () => {
    setLoading(true);
    try {
      const params = {};
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      const response = await axios.get(`${API}/pick-pool`, { params });
      setPickPoolData(response.data);
    } catch (error) {
      toast.error("Failed to load Pick Pool");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openLfbDialog = (lead, e) => {
    e.stopPropagation();
    setSelectedLead(lead);
    setLfbId(lead.lfb_id || "");
    setLfbDialogOpen(true);
  };

  const submitLfbId = async () => {
    if (!lfbId.trim()) {
      toast.error("Please enter the LFB ID");
      return;
    }
    
    setSubmittingLfb(true);
    try {
      await axios.post(`${API}/pick-pool/${selectedLead.id}/submit-lfb`, {
        lfb_id: lfbId.trim()
      });
      toast.success("LFB ID submitted successfully!");
      setLfbDialogOpen(false);
      setLfbId("");
      setSelectedLead(null);
      fetchPickPool(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit LFB ID");
    } finally {
      setSubmittingLfb(false);
    }
  };

  const formatFieldName = (field) => {
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusBadge = (status) => {
    const colors = {
      new: "bg-blue-100 text-blue-700",
      contacted: "bg-yellow-100 text-yellow-700",
      qualified: "bg-green-100 text-green-700",
      converted_to_lfb: "bg-purple-100 text-purple-700",
      lost: "bg-red-100 text-red-700"
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  return (
    <div className="space-y-6" data-testid="pick-pool-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ListChecks className="w-7 h-7 text-green-600" />
            Pick Pool
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Leads with all mandatory fields filled - ready for processing
          </p>
        </div>
        <Button variant="outline" onClick={fetchPickPool} className="gap-2" data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-xl">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">In Pick Pool</p>
                <p className="text-3xl font-bold text-green-700">
                  {pickPoolData?.pick_pool_count || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-xl">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-orange-600 font-medium">Pending (Incomplete)</p>
                <p className="text-3xl font-bold text-orange-700">
                  {pickPoolData?.pending_count || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Leads</p>
                <p className="text-3xl font-bold text-blue-700">
                  {pickPoolData?.total_count || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, company, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            {debouncedSearch && (
              <Badge variant="secondary">
                {pickPoolData?.leads?.length || 0} results
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Required Fields Info */}
      <Card className="border-dashed border-slate-300 bg-slate-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-slate-600 flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Mandatory Fields for Pick Pool
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {pickPoolData?.required_fields?.map((field) => (
              <Badge key={field} variant="outline" className="text-xs bg-white">
                {formatFieldName(field)} *
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            Pick Pool Leads
            <Badge className="bg-green-100 text-green-700 ml-2">
              {pickPoolData?.leads?.length || 0}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-green-500" />
            </div>
          ) : !pickPoolData?.leads?.length ? (
            <div className="text-center py-12 text-slate-500">
              <ListChecks className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="font-medium">No leads in Pick Pool yet</p>
              <p className="text-sm mt-1">
                Complete all mandatory fields on leads to add them to Pick Pool
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate('/leads')}
              >
                Go to Leads
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Lead</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>LFB Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pickPoolData.leads.map((lead) => (
                    <TableRow 
                      key={lead.id} 
                      className="hover:bg-green-50/50 cursor-pointer"
                      onClick={() => navigate(`/leads/${lead.id}`)}
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{lead.name}</p>
                            <p className="text-xs text-slate-500">{lead.city}, {lead.region}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{lead.company_name || '-'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{lead.phone}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{lead.assigned_to_name || 'Unassigned'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <Badge variant="outline" className="text-xs">
                            {lead.product_category}
                          </Badge>
                          {lead.fabric_type && (
                            <span className="text-xs text-slate-500 ml-2">
                              {lead.fabric_type}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium">
                          {lead.quantity} {lead.quantity_unit}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadge(lead.status)}>
                          {lead.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {lead.lfb_created ? (
                          <div className="flex flex-col gap-1">
                            <Badge className="bg-blue-100 text-blue-700 gap-1">
                              <Check className="w-3 h-3" />
                              {lead.lfb_id}
                            </Badge>
                            {lead.lfb_submitted_by && (
                              <span className="text-xs text-slate-500">by {lead.lfb_submitted_by}</span>
                            )}
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-slate-500">
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Only non-admin users can submit LFB ID */}
                          {!lead.lfb_created && !isAdmin && (
                            <Button 
                              size="sm" 
                              className="gap-1 bg-blue-600 hover:bg-blue-700"
                              onClick={(e) => openLfbDialog(lead, e)}
                              data-testid={`submit-lfb-btn-${lead.id}`}
                            >
                              <ExternalLink className="w-3 h-3" />
                              Submit LFB ID
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/leads/${lead.id}`);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* LFB ID Submission Dialog */}
      <Dialog open={lfbDialogOpen} onOpenChange={setLfbDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-blue-600" />
              Submit Locofast App ID
            </DialogTitle>
            <DialogDescription>
              Enter the LFB ID after creating the lead in Locofast App
            </DialogDescription>
          </DialogHeader>
          
          {selectedLead && (
            <div className="space-y-4 py-4">
              {/* Lead Info */}
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{selectedLead.name}</p>
                    <p className="text-sm text-slate-500">{selectedLead.company_name}</p>
                  </div>
                </div>
              </div>
              
              {/* LFB ID Input */}
              <div className="space-y-2">
                <Label htmlFor="lfb-id" className="text-sm font-medium">
                  LFB ID <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="lfb-id"
                  placeholder="Enter Locofast App ID (e.g., LFB-12345)"
                  value={lfbId}
                  onChange={(e) => setLfbId(e.target.value)}
                  data-testid="lfb-id-input"
                  className="w-full"
                />
                <p className="text-xs text-slate-500">
                  This confirms the lead has been created in Locofast App
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setLfbDialogOpen(false);
                setLfbId("");
                setSelectedLead(null);
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={submitLfbId}
              disabled={submittingLfb || !lfbId.trim()}
              data-testid="submit-lfb-confirm-btn"
            >
              {submittingLfb ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Submit LFB ID
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PickPool;
